import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import VisibilityRounded from "@mui/icons-material/VisibilityRounded";
import VisibilityOffRounded from "@mui/icons-material/VisibilityOffRounded";
import { useAuth } from "@bw/shared/hooks";
import { getAuthErrorMessage } from "@bw/shared/auth-errors";
import { GoogleButton } from "./GoogleButton";
import {
  signInSchema,
  signUpSchema,
  resetSchema,
  type SignInValues,
  type SignUpValues,
  type ResetValues,
} from "../schemas";
import { visuallyHidden } from "../../../common/visuallyHidden";

export type AuthMode = "signIn" | "signUp" | "reset";

type FormValues = SignInValues & SignUpValues & ResetValues;

const SCHEMA = {
  signIn: signInSchema,
  signUp: signUpSchema,
  reset: resetSchema,
} as const;

const SUBMIT_LABEL: Record<AuthMode, string> = {
  signIn: "Sign in",
  signUp: "Create account",
  reset: "Send recovery link",
};

const SECTION_HEADING: Record<AuthMode, string> = {
  signIn: "Sign in",
  signUp: "Create your account",
  reset: "Recover your password",
};

/**
 * Email/password/Google auth form backed by the existing RHF, Zod, and shared
 * authentication contracts. MUI owns presentation only; navigation and auth
 * transitions intentionally remain unchanged.
 */
export function AuthForm({
  mode,
  intro,
}: {
  mode: AuthMode;
  /** One muted line under a compact task heading (the approved sign-in). */
  intro?: string;
}) {
  const {
    authError,
    signIn,
    signInWithEmail,
    signUpPendingVerification,
    resetPassword,
  } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [formError, setFormError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [showPw, setShowPw] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    resolver: zodResolver(SCHEMA[mode] as never),
    mode: "onSubmit",
  });

  const onSubmit = handleSubmit(async (values) => {
    setFormError(null);
    setSuccess(null);
    try {
      if (mode === "reset") {
        await resetPassword?.(values.email);
        setSuccess(
          "If that email is registered, a password reset link is on its way — check your inbox.",
        );
        return;
      }
      if (mode === "signUp") {
        const returnUrl = searchParams.get("returnUrl") ?? undefined;
        const verificationStatus = await signUpPendingVerification?.(
          values.email,
          values.password,
          values.accountName || undefined,
          returnUrl,
        );
        const verifyParams = new URLSearchParams({ email: values.email });
        const deliveryStatus =
          typeof verificationStatus === "object"
            ? verificationStatus.deliveryStatus
            : verificationStatus;
        if (deliveryStatus === "delivery_failed") {
          verifyParams.set("delivery", "failed");
        }
        if (
          typeof verificationStatus === "object" &&
          verificationStatus.accountNameIncomplete
        ) {
          verifyParams.set("name", "incomplete");
        }
        if (returnUrl) verifyParams.set("returnUrl", returnUrl);
        navigate(`/verify-email?${verifyParams.toString()}`);
        return;
      }
      await signInWithEmail?.(values.email, values.password);
    } catch (error) {
      setFormError(getAuthErrorMessage(error));
    }
  });

  const handleGoogle = async () => {
    setFormError(null);
    setSuccess(null);
    try {
      await signIn();
    } catch (error) {
      setFormError(getAuthErrorMessage(error));
    }
  };

  const passwordAdornment = (
    <InputAdornment position="end">
      <IconButton
        type="button"
        edge="end"
        size="small"
        aria-pressed={showPw}
        aria-label={showPw ? "Hide password" : "Show password"}
        onClick={() => setShowPw((value) => !value)}
      >
        {showPw ? (
          <VisibilityOffRounded aria-hidden="true" />
        ) : (
          <VisibilityRounded aria-hidden="true" />
        )}
      </IconButton>
    </InputAdornment>
  );

  const confirmAdornment = (
    <InputAdornment position="end">
      <IconButton
        type="button"
        edge="end"
        size="small"
        aria-pressed={showConfirm}
        aria-label={showConfirm ? "Hide password again" : "Show password again"}
        onClick={() => setShowConfirm((value) => !value)}
      >
        {showConfirm ? (
          <VisibilityOffRounded aria-hidden="true" />
        ) : (
          <VisibilityRounded aria-hidden="true" />
        )}
      </IconButton>
    </InputAdornment>
  );

  return (
    <Stack component="section" aria-labelledby="auth-h" sx={{ minWidth: 0 }}>
      <Typography
        component="h2"
        variant="h2"
        id="auth-h"
        sx={
          mode === "reset"
            ? visuallyHidden
            : intro
              ? { fontSize: "1.375rem", fontWeight: 700, lineHeight: 1.25 }
              : { fontSize: "1.625rem", lineHeight: 1.25, mb: 3.5 }
        }
      >
        {SECTION_HEADING[mode]}
      </Typography>
      {intro ? (
        <Typography color="text.secondary" variant="body2" sx={{ mb: 2.5 }}>
          {intro}
        </Typography>
      ) : null}

      <Stack component="form" onSubmit={onSubmit} noValidate spacing={2.5}>
        {(formError ?? authError) ? (
          <Alert severity="error" role="alert">
            {formError ?? authError}
          </Alert>
        ) : null}
        {success ? (
          <Alert severity="success" role="status">
            {success}
          </Alert>
        ) : null}

        {mode === "signUp" ? (
          <TextField
            label="Account name (optional)"
            autoComplete="nickname"
            helperText="Used for your sign-in account. You can set it later in Edit profile."
            {...register("accountName")}
          />
        ) : null}
        <TextField
          label="Email"
          type="email"
          inputMode="email"
          autoComplete="email"
          placeholder="you@example.com"
          slotProps={{ inputLabel: { shrink: true } }}
          error={Boolean(errors.email)}
          helperText={errors.email?.message}
          {...register("email")}
        />

        {mode !== "reset" ? (
          <TextField
            label="Password"
            type={showPw ? "text" : "password"}
            autoComplete={
              mode === "signUp" ? "new-password" : "current-password"
            }
            placeholder="At least 8 characters"
            error={Boolean(errors.password)}
            helperText={errors.password?.message}
            slotProps={{
              input: { endAdornment: passwordAdornment },
              inputLabel: { shrink: true },
            }}
            {...register("password")}
          />
        ) : null}

        {mode === "signUp" ? (
          <TextField
            label="Confirm password"
            type={showConfirm ? "text" : "password"}
            autoComplete="new-password"
            placeholder="Re-enter your password"
            error={Boolean(errors.confirmPassword)}
            helperText={errors.confirmPassword?.message}
            slotProps={{ input: { endAdornment: confirmAdornment } }}
            {...register("confirmPassword")}
          />
        ) : null}

        <Button
          type="submit"
          variant="contained"
          size="small"
          fullWidth
          disabled={isSubmitting}
          sx={{ borderRadius: "10px" }}
          startIcon={
            isSubmitting ? (
              <CircularProgress size={16} color="inherit" aria-hidden="true" />
            ) : undefined
          }
        >
          {SUBMIT_LABEL[mode]}
        </Button>

        {mode !== "reset" ? (
          <>
            <Divider sx={{ color: "text.secondary", fontSize: "0.75rem" }}>
              or
            </Divider>
            <GoogleButton onClick={handleGoogle} disabled={isSubmitting} />
          </>
        ) : null}
      </Stack>
    </Stack>
  );
}
